<template>
    <v-row class="mt-2">
        <v-col cols="12" align="center">
            <v-progress-circular v-if="loading" indeterminate color="teal" size="48" />

            <v-alert
                v-else-if="errorMessage"
                type="warning"
                outlined
                class="mx-auto"
                max-width="900"
            >
                {{ errorMessage }}
            </v-alert>

            <v-alert
                v-else-if="sortedMatches.length === 0"
                type="info"
                outlined
                class="mx-auto"
                max-width="900"
            >
                {{ emptyMessage }}
            </v-alert>

            <div v-else class="bracket-wrapper">
                <svg
                    v-if="connectorPaths.length"
                    class="bracket-connectors"
                    :width="connectorCanvas.width"
                    :height="connectorCanvas.height"
                >
                    <path
                        v-for="(path, index) in connectorPaths"
                        :key="`path-${index}`"
                        :d="path.d"
                        :class="[
                            'connector-line',
                            {
                                'connector-line-active': isPathActive(path),
                                'connector-line-dimmed': hoveredTeamId && !isPathActive(path)
                            }
                        ]"
                    />
                </svg>

                <table class="bracket-table">
                    <tr>
                        <th
                            v-for="round in rounds"
                            :key="`h-${round}`"
                        >
                            <v-card color="custom-teal" class="white--text px-4 py-2 text-center">
                                {{ getRoundName(round) }}
                            </v-card>
                        </th>
                    </tr>
                    <tr>
                        <td
                            v-for="round in rounds"
                            :key="`c-${round}`"
                            :data-round="round"
                            :style="getRoundColumnStyle(round)"
                        >
                            <div
                                v-for="match in bracketByRound[round]"
                                :key="match.id"
                                :class="[
                                    'bracket-card',
                                    { 'my-5': isFirstRound(round) },
                                    {
                                        'bracket-card-active': isCardActive(match.id),
                                        'bracket-card-dimmed': hoveredTeamId && !isCardActive(match.id)
                                    }
                                ]"
                                :style="getBracketCardStyle(round, match)"
                                :data-match-id="match.id"
                            >
                                <graph-match-card
                                    :match="match"
                                    @team-hover="setHoveredTeam"
                                    @team-leave="clearHoveredTeam"
                                />
                            </div>
                        </td>
                    </tr>
                </table>
            </div>
        </v-col>
    </v-row>
</template>

<script>
    import GraphMatchCard from '@/components/GraphMatchCard'

    export default {
        components: { GraphMatchCard },
        props: {
            loading: {
                type: Boolean,
                default: false
            },
            errorMessage: {
                type: String,
                default: ''
            },
            matches: {
                type: Array,
                default: () => []
            },
            emptyMessage: {
                type: String,
                default: 'Δεν βρέθηκαν αγώνες για την τρέχουσα σεζόν και το επιλεγμένο Cup League.'
            }
        },
        data() {
            return {
                sortedMatches: [],
                rounds: [],
                bracketByRound: {},
                connectorPaths: [],
                connectorCanvas: {
                    width: 0,
                    height: 0
                },
                connectorRedrawTimer: null,
                roundLinks: [],
                hoveredTeamId: null,
                activeMatchMap: {},
                activeLinkMap: {},
                roundColumnHeight: null,
                matchTopById: {}
            }
        },
        watch: {
            matches: {
                immediate: true,
                deep: true,
                handler(newMatches) {
                    this.buildBracket(Array.isArray(newMatches) ? newMatches : [])
                }
            },
            loading(value) {
                if (value) {
                    this.resetVisualState()
                }
            }
        },
        mounted() {
            window.addEventListener('resize', this.scheduleConnectorRedraw)
        },
        beforeDestroy() {
            window.removeEventListener('resize', this.scheduleConnectorRedraw)
            if (this.connectorRedrawTimer) {
                clearTimeout(this.connectorRedrawTimer)
            }
        },
        methods: {
            resetVisualState() {
                this.connectorPaths = []
                this.roundLinks = []
                this.hoveredTeamId = null
                this.activeMatchMap = {}
                this.activeLinkMap = {}
                this.roundColumnHeight = null
                this.matchTopById = {}
            },
            getRoundName(round) {
                const roundMatch = this.bracketByRound[round]?.find(match => match?.customRoundText)
                if (roundMatch?.customRoundText) return roundMatch.customRoundText

                if (this.rounds.length === 0) return `Round ${round}`
                const finalRound = this.rounds[this.rounds.length - 1]
                if (round === finalRound) return 'Final'
                if (round === finalRound - 1) return 'Semi-Finals'
                if (round === finalRound - 2) return 'Quarter-Finals'
                return `Round ${round}`
            },
            normalizeRound(round) {
                const direct = Number(round)
                if (Number.isFinite(direct) && direct > 0) return direct

                const extracted = String(round || '').match(/\d+/)
                if (extracted) {
                    const parsed = Number(extracted[0])
                    if (Number.isFinite(parsed) && parsed > 0) return parsed
                }

                return 1
            },
            getTeamId(team) {
                return Number(team?.id || team?.teamId || team?.team_id || 0)
            },
            getMatchId(match) {
                return Number(match?.id || 0)
            },
            getTeamLane(match, teamId) {
                if (!teamId) return 0

                const team1Id = this.getTeamId(match?.team1)
                const team2Id = this.getTeamId(match?.team2)
                if (team1Id === teamId) return 1
                if (team2Id === teamId) return 2
                return 0
            },
            isFirstRound(round) {
                return round === this.rounds[0]
            },
            getRoundColumnStyle(round) {
                if (this.isFirstRound(round) || !this.roundColumnHeight) {
                    return { verticalAlign: 'middle' }
                }

                return {
                    position: 'relative',
                    height: `${this.roundColumnHeight}px`,
                    verticalAlign: 'top'
                }
            },
            getBracketCardStyle(round, match) {
                if (this.isFirstRound(round) || !this.roundColumnHeight) return {}

                const top = this.matchTopById[this.getMatchId(match)]
                if (!Number.isFinite(top)) return {}

                return {
                    position: 'absolute',
                    top: `${top}px`,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    margin: 0
                }
            },
            collectCardPositions(wrapper) {
                const cardElements = Array.from(wrapper.querySelectorAll('.bracket-card'))
                const wrapperRect = wrapper.getBoundingClientRect()
                const positionsByMatchId = {}

                cardElements.forEach(card => {
                    const matchId = Number(card.dataset.matchId || 0)
                    if (!matchId) return

                    const visualCard = card.querySelector('.v-card')
                    const visualRect = visualCard ? visualCard.getBoundingClientRect() : card.getBoundingClientRect()
                    const x = visualRect.left - wrapperRect.left + wrapper.scrollLeft
                    const y = visualRect.top - wrapperRect.top + wrapper.scrollTop

                    positionsByMatchId[matchId] = {
                        left: x,
                        right: x + visualRect.width,
                        top: y,
                        height: visualRect.height,
                        centerY: y + visualRect.height / 2
                    }
                })

                return positionsByMatchId
            },
            updateRoundColumnLayout(wrapper) {
                if (!wrapper || this.rounds.length === 0) {
                    const hadState = this.roundColumnHeight !== null || Object.keys(this.matchTopById).length > 0
                    this.roundColumnHeight = null
                    this.matchTopById = {}
                    return hadState
                }

                const firstRound = this.rounds[0]
                const firstRoundColumn = wrapper.querySelector(`td[data-round='${firstRound}']`)
                const measuredHeight = Math.ceil(firstRoundColumn?.scrollHeight || 0)
                const nextColumnHeight = measuredHeight > 0 ? measuredHeight : null
                const positionsByMatchId = this.collectCardPositions(wrapper)

                const nextTopMap = {}
                const minGap = 12

                for (let index = 1; index < this.rounds.length; index++) {
                    const previousRound = this.rounds[index - 1]
                    const currentRound = this.rounds[index]
                    const previousMatches = this.bracketByRound[previousRound] || []
                    const currentMatches = this.bracketByRound[currentRound] || []
                    if (currentMatches.length === 0 || !nextColumnHeight) continue

                    const currentRoundColumn = wrapper.querySelector(`td[data-round='${currentRound}']`)
                    const wrapperRect = wrapper.getBoundingClientRect()
                    const columnRect = currentRoundColumn?.getBoundingClientRect()
                    const columnTop = columnRect
                        ? (columnRect.top - wrapperRect.top + wrapper.scrollTop)
                        : 0

                    const provisional = currentMatches.map((match, currentIndex) => {
                        const matchId = this.getMatchId(match)
                        const firstFeeder = previousMatches[currentIndex * 2]
                        const secondFeeder = previousMatches[(currentIndex * 2) + 1] || firstFeeder

                        const feederCenterA = positionsByMatchId[this.getMatchId(firstFeeder)]?.centerY
                        const feederCenterB = positionsByMatchId[this.getMatchId(secondFeeder)]?.centerY

                        const ownHeight = positionsByMatchId[matchId]?.height || 84
                        const desiredCenter = (Number.isFinite(feederCenterA) && Number.isFinite(feederCenterB))
                            ? (feederCenterA + feederCenterB) / 2
                            : (positionsByMatchId[matchId]?.centerY || (ownHeight / 2))
                        const unclampedTop = (desiredCenter - (ownHeight / 2)) - columnTop
                        const maxTop = Math.max(0, nextColumnHeight - ownHeight)

                        return {
                            matchId,
                            height: ownHeight,
                            top: Math.min(maxTop, Math.max(0, unclampedTop))
                        }
                    })

                    provisional.sort((a, b) => a.top - b.top)
                    for (let i = 1; i < provisional.length; i++) {
                        const prev = provisional[i - 1]
                        const current = provisional[i]
                        const minTop = prev.top + prev.height + minGap
                        if (current.top < minTop) current.top = minTop
                    }

                    for (let i = provisional.length - 2; i >= 0; i--) {
                        const current = provisional[i]
                        const next = provisional[i + 1]
                        const maxTop = Math.max(0, next.top - current.height - minGap)
                        if (current.top > maxTop) current.top = maxTop
                    }

                    provisional.forEach(item => {
                        nextTopMap[item.matchId] = item.top
                    })
                }

                const heightChanged = this.roundColumnHeight !== nextColumnHeight
                const mapChanged = JSON.stringify(this.matchTopById) !== JSON.stringify(nextTopMap)

                this.roundColumnHeight = nextColumnHeight
                this.matchTopById = nextTopMap

                return heightChanged || mapChanged
            },
            getNextMatchOrderKey(match, nextRoundMatches) {
                const teamIds = [
                    this.getTeamId(match.team1),
                    this.getTeamId(match.team2)
                ].filter(Boolean)

                if (teamIds.length === 0 || nextRoundMatches.length === 0) {
                    return { nextMatchIndex: Number.MAX_SAFE_INTEGER, lane: Number.MAX_SAFE_INTEGER }
                }

                for (let index = 0; index < nextRoundMatches.length; index++) {
                    const nextMatch = nextRoundMatches[index]
                    const sharedTeamId = teamIds.find(teamId => {
                        return this.getTeamLane(nextMatch, teamId) > 0
                    })

                    if (sharedTeamId) {
                        return {
                            nextMatchIndex: index,
                            lane: this.getTeamLane(nextMatch, sharedTeamId)
                        }
                    }
                }

                return { nextMatchIndex: Number.MAX_SAFE_INTEGER, lane: Number.MAX_SAFE_INTEGER }
            },
            orderRoundMatches(rounds, grouped) {
                if (rounds.length === 0) return grouped

                const orderedGrouped = {}
                const firstRound = rounds[0]
                orderedGrouped[firstRound] = [...(grouped[firstRound] || [])]

                for (let index = 1; index < rounds.length; index++) {
                    const currentRound = rounds[index]
                    const previousRound = rounds[index - 1]
                    const previousMatches = orderedGrouped[previousRound] || []
                    const currentMatches = [...(grouped[currentRound] || [])]

                    if (previousMatches.length === 0 || currentMatches.length <= 1) {
                        orderedGrouped[currentRound] = currentMatches
                        continue
                    }

                    const previousIndexById = {}
                    previousMatches.forEach((match, previousIndex) => {
                        previousIndexById[this.getMatchId(match)] = previousIndex
                    })

                    const previousTeamIdsByMatchId = {}
                    previousMatches.forEach(match => {
                        const matchId = this.getMatchId(match)
                        if (!matchId) return

                        previousTeamIdsByMatchId[matchId] = [
                            this.getTeamId(match.team1),
                            this.getTeamId(match.team2)
                        ].filter(Boolean)
                    })

                    currentMatches.sort((a, b) => {
                        const scoreA = this.getParentOrderScore(a, previousMatches, previousIndexById, previousTeamIdsByMatchId)
                        const scoreB = this.getParentOrderScore(b, previousMatches, previousIndexById, previousTeamIdsByMatchId)

                        if (scoreA !== scoreB) return scoreA - scoreB

                        const dateA = new Date(a.date || 0).getTime()
                        const dateB = new Date(b.date || 0).getTime()
                        if (dateA !== dateB) return dateA - dateB

                        return this.getMatchId(a) - this.getMatchId(b)
                    })

                    orderedGrouped[currentRound] = currentMatches
                }

                for (let index = rounds.length - 2; index >= 0; index--) {
                    const currentRound = rounds[index]
                    const nextRound = rounds[index + 1]
                    const currentMatches = [...(orderedGrouped[currentRound] || [])]
                    const nextRoundMatches = orderedGrouped[nextRound] || []

                    if (currentMatches.length <= 1 || nextRoundMatches.length === 0) continue

                    const originalIndexById = {}
                    currentMatches.forEach((match, originalIndex) => {
                        originalIndexById[this.getMatchId(match)] = originalIndex
                    })

                    currentMatches.sort((a, b) => {
                        const keyA = this.getNextMatchOrderKey(a, nextRoundMatches)
                        const keyB = this.getNextMatchOrderKey(b, nextRoundMatches)

                        if (keyA.nextMatchIndex !== keyB.nextMatchIndex) {
                            return keyA.nextMatchIndex - keyB.nextMatchIndex
                        }

                        if (keyA.lane !== keyB.lane) {
                            return keyA.lane - keyB.lane
                        }

                        return originalIndexById[this.getMatchId(a)] - originalIndexById[this.getMatchId(b)]
                    })

                    orderedGrouped[currentRound] = currentMatches
                }

                return orderedGrouped
            },
            getParentOrderScore(currentMatch, previousMatches, previousIndexById, previousTeamIdsByMatchId) {
                const currentTeamIds = [
                    this.getTeamId(currentMatch.team1),
                    this.getTeamId(currentMatch.team2)
                ].filter(Boolean)

                if (currentTeamIds.length === 0) return Number.MAX_SAFE_INTEGER

                const parentIndexes = []

                previousMatches.forEach(previousMatch => {
                    const previousMatchId = this.getMatchId(previousMatch)
                    const previousTeamIds = previousTeamIdsByMatchId[previousMatchId] || []
                    const sharesTeam = currentTeamIds.some(teamId => previousTeamIds.includes(teamId))
                    if (!sharesTeam) return

                    const index = previousIndexById[previousMatchId]
                    if (Number.isInteger(index)) {
                        parentIndexes.push(index)
                    }
                })

                if (parentIndexes.length === 0) return Number.MAX_SAFE_INTEGER

                const total = parentIndexes.reduce((sum, value) => sum + value, 0)
                return total / parentIndexes.length
            },
            getRoundLinks() {
                if (this.rounds.length < 2) return []

                const links = []
                const uniqueLinks = new Set()

                this.rounds.forEach(round => {
                    const previousRound = round - 1
                    if (!this.bracketByRound[previousRound] || !this.bracketByRound[round]) return

                    const previousMatches = this.bracketByRound[previousRound]
                    const currentMatches = this.bracketByRound[round]

                    currentMatches.forEach(currentMatch => {
                        const currentMatchId = this.getMatchId(currentMatch)
                        const currentTeamIds = [this.getTeamId(currentMatch.team1), this.getTeamId(currentMatch.team2)].filter(Boolean)

                        if (!currentMatchId || currentTeamIds.length === 0) return

                        previousMatches.forEach(previousMatch => {
                            const previousMatchId = this.getMatchId(previousMatch)
                            const previousTeamIds = [this.getTeamId(previousMatch.team1), this.getTeamId(previousMatch.team2)].filter(Boolean)

                            if (!previousMatchId || previousTeamIds.length === 0) return

                            const sharedTeamIds = currentTeamIds.filter(teamId => previousTeamIds.includes(teamId))
                            if (sharedTeamIds.length === 0) return

                            sharedTeamIds.forEach(teamId => {
                                const fromLane = this.getTeamLane(previousMatch, teamId)
                                const toLane = this.getTeamLane(currentMatch, teamId)
                                const key = `${previousMatchId}-${currentMatchId}-${teamId}`

                                if (!fromLane || !toLane || uniqueLinks.has(key)) return

                                uniqueLinks.add(key)
                                links.push({
                                    from: previousMatchId,
                                    to: currentMatchId,
                                    fromLane,
                                    toLane,
                                    teamId
                                })
                            })
                        })
                    })
                })

                return links
            },
            scheduleConnectorRedraw() {
                if (this.connectorRedrawTimer) {
                    clearTimeout(this.connectorRedrawTimer)
                }

                this.connectorRedrawTimer = setTimeout(() => {
                    this.drawConnectors()
                }, 0)
            },
            setHoveredTeam(payload) {
                const parsedTeamId = Number(payload?.teamId || 0)
                if (!parsedTeamId) return

                this.hoveredTeamId = parsedTeamId
                this.updateActivePathState()
                this.scheduleConnectorRedraw()
            },
            clearHoveredTeam() {
                this.hoveredTeamId = null
                this.activeMatchMap = {}
                this.activeLinkMap = {}
                this.scheduleConnectorRedraw()
            },
            updateActivePathState() {
                if (!this.hoveredTeamId) {
                    this.activeMatchMap = {}
                    this.activeLinkMap = {}
                    return
                }

                const activeMatchMap = {}

                this.sortedMatches.forEach(match => {
                    const matchId = this.getMatchId(match)
                    const team1Id = this.getTeamId(match?.team1)
                    const team2Id = this.getTeamId(match?.team2)
                    if (matchId && (team1Id === this.hoveredTeamId || team2Id === this.hoveredTeamId)) {
                        activeMatchMap[matchId] = true
                    }
                })

                const activeLinkMap = {}
                this.roundLinks.forEach(link => {
                    if (link.teamId === this.hoveredTeamId) {
                        const key = `${link.from}-${link.to}-${link.fromLane}-${link.toLane}`
                        activeLinkMap[key] = true
                        activeMatchMap[link.from] = true
                        activeMatchMap[link.to] = true
                    }
                })

                this.activeMatchMap = activeMatchMap
                this.activeLinkMap = activeLinkMap
            },
            isCardActive(matchId) {
                const parsedId = Number(matchId || 0)
                if (!parsedId || !this.hoveredTeamId) return false
                return Boolean(this.activeMatchMap[parsedId])
            },
            isPathActive(path) {
                if (!this.hoveredTeamId || !path?.key) return false
                return Boolean(this.activeLinkMap[path.key])
            },
            drawConnectors() {
                const wrapper = this.$el?.querySelector('.bracket-wrapper')
                if (!wrapper || this.sortedMatches.length === 0) {
                    this.connectorPaths = []
                    this.roundLinks = []
                    return
                }

                const links = this.getRoundLinks()
                this.roundLinks = links

                const layoutChanged = this.updateRoundColumnLayout(wrapper)
                if (layoutChanged) {
                    this.$nextTick(() => this.scheduleConnectorRedraw())
                    return
                }

                if (links.length === 0) {
                    this.connectorPaths = []
                    this.activeLinkMap = {}
                    return
                }

                const positionsByMatchId = this.collectCardPositions(wrapper)

                const paths = links
                    .map(link => {
                        const from = positionsByMatchId[link.from]
                        const to = positionsByMatchId[link.to]

                        if (!from || !to) return null

                        const startX = from.right
                        const startY = from.centerY
                        const endX = to.left
                        const endY = to.centerY
                        const middleX = startX + ((endX - startX) / 2)
                        const d = `M ${startX} ${startY} L ${middleX} ${startY} L ${middleX} ${endY} L ${endX} ${endY}`
                        const key = `${link.from}-${link.to}-${link.fromLane}-${link.toLane}`

                        return {
                            d,
                            key
                        }
                    })
                    .filter(Boolean)

                this.connectorCanvas = {
                    width: Math.max(wrapper.scrollWidth, wrapper.clientWidth),
                    height: Math.max(wrapper.scrollHeight, wrapper.clientHeight)
                }
                this.connectorPaths = paths
                this.updateActivePathState()
            },
            buildBracket(matches) {
                const sorted = [...matches].sort((a, b) => {
                    const roundDiff = this.normalizeRound(a.round) - this.normalizeRound(b.round)
                    if (roundDiff !== 0) return roundDiff
                    const dateA = new Date(a.date || 0).getTime()
                    const dateB = new Date(b.date || 0).getTime()
                    return dateA - dateB
                })

                this.sortedMatches = sorted
                this.rounds = [...new Set(sorted.map(match => this.normalizeRound(match.round)))].filter(round => round > 0)

                const grouped = {}
                this.rounds.forEach(round => {
                    grouped[round] = sorted.filter(match => this.normalizeRound(match.round) === round)
                })

                this.bracketByRound = this.orderRoundMatches(this.rounds, grouped)
                this.$nextTick(() => {
                    this.scheduleConnectorRedraw()
                    setTimeout(() => this.scheduleConnectorRedraw(), 250)
                })
            }
        }
    }
</script>

<style scoped>
    .bracket-wrapper {
        position: relative;
        max-height: 100%;
        overflow: auto;
        padding-bottom: 12px;
    }

    .bracket-table {
        border-collapse: separate;
        border-spacing: 48px 0;
        width: max-content;
        margin: 0 auto;
    }

    .bracket-connectors {
        position: absolute;
        top: 0;
        left: 0;
        pointer-events: none;
        z-index: 4;
    }

    .connector-line {
        fill: none;
        stroke: #00897b;
        stroke-width: 2;
        opacity: 0.9;
        transition: opacity 0.16s ease, stroke-width 0.16s ease;
    }

    .connector-line-active {
        opacity: 1;
        stroke-width: 3;
    }

    .connector-line-dimmed {
        opacity: 0.14;
    }

    .bracket-card {
        position: relative;
        z-index: 2;
        display: flex;
        justify-content: center;
        transition: opacity 0.16s ease;
    }

    .bracket-card /deep/ .v-card {
        background-color: rgb(46, 46, 46) !important;
        padding: 8px !important;
        color: white;
    }

    .bracket-card /deep/ .v-divider {
        border-color: rgba(255, 255, 255, 0.65) !important;
        opacity: 1 !important;
        margin: 5px 0 !important;
    }

    .bracket-card-active {
        opacity: 1;
    }

    .bracket-card-active /deep/ .v-card {
        box-shadow: 0 0 0 2px rgba(0, 137, 123, 0.45), 0 8px 18px rgba(0, 0, 0, 0.12);
    }

    .bracket-card /deep/ .row {
        justify-content: flex-start !important;
    }

    .bracket-card /deep/ .col,
    .bracket-card /deep/ .text-center {
        text-align: left !important;
    }

    .bracket-card /deep/ [align='center'],
    .bracket-card /deep/ .v-row > [class*='col-'] {
        text-align: left !important;
        justify-content: flex-start !important;
    }

    .bracket-card-dimmed {
        opacity: 0.32;
    }

    th,
    td {
        padding: 0;
        min-width: 320px;
        text-align: center;
    }
</style>
